import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { PremiumFooter } from '@/components/premium/PremiumFooter';
import { ProductCard } from '@/components/ProductCard';
import { SEO, BreadcrumbStructuredData, LocalBusinessStructuredData, ItemListStructuredData } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';
import { MapPin, Phone, Mail, Truck, Clock, ShieldCheck, Factory, CheckCircle, ArrowRight, Building2, Award, Wrench } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const SITE_URL = 'https://www.modularfurniturestore.com';

const LOCATION_DATA = {
  'bkc-mumbai': {
    slug: 'office-furniture-bkc-mumbai',
    city: 'BKC Mumbai',
    fullCity: 'Bandra Kurla Complex, Mumbai',
    state: 'Maharashtra',
    tier: 'Premium Financial & Corporate Headquarters',
    heroTagline: 'Premium Office Furniture for BKC\'s Financial & Corporate District',
    heroDescription: 'Modular workstations, acoustic meeting pods, and executive storage systems — manufactured at our Bhiwandi facility and delivered directly to BKC\'s premium corporate towers. Trusted by banks, financial institutions, and Fortune 500 offices.',
    microMarkets: ['Bandra Kurla Complex', 'BKC Annexe', 'MMRDA Zone', 'Platina & Crescenzo', 'Diamond District'],
    targetAudience: 'Banks, Asset Management Firms, Global Consulting Offices, Insurance HQs, NBFC Corporate Floors',
    keyIndustries: ['Banking & Financial Services', 'Asset Management & PE Firms', 'Global Consulting (Big 4)', 'Insurance Headquarters', 'Corporate Law Firms'],
    distanceFromFactory: '~38 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Certified on-site installation team available for after-hours & weekend deployments in BKC towers',
    seoTitle: 'Office Furniture BKC Mumbai | Modular Workstations for Corporate HQs | MFS',
    seoDescription: 'Buy premium modular office furniture for BKC Mumbai. Factory-direct workstations, acoustic pods & storage from Bhiwandi hub. Express 10-14 day delivery. ISO 9001:2015 & IGBC certified. Free on-site installation.',
    seoKeywords: 'office furniture bkc mumbai, modular workstations bkc, acoustic pods bandra kurla complex, corporate furniture bkc, office furniture near bkc mumbai',
    faqPageKey: 'locationBkcMumbai',
    interlinkKey: 'locationBkcMumbai',
  },
  'andheri-powai': {
    slug: 'office-furniture-andheri-powai',
    city: 'Andheri & Powai',
    fullCity: 'Andheri East, Powai, MIDC Andheri',
    state: 'Maharashtra',
    tier: 'Tech Clusters, BPO & SME Corporate Parks',
    heroTagline: 'Office Furniture for Andheri–Powai Tech & BPO Corridor',
    heroDescription: 'Scalable modular workstations, privacy booths, and high-density storage — engineered for the fast-growing tech parks, BPO centres, and SME campuses of Andheri and Powai. Direct from our Bhiwandi factory, installed on your floor.',
    microMarkets: ['Andheri East MIDC', 'Powai Hiranandani', 'Chandivali', 'Marol MIDC', 'Saki Naka Business Zone'],
    targetAudience: 'IT/BPO Firms, SME Corporates, Startup Hubs, Co-working Spaces, EdTech Companies',
    keyIndustries: ['IT Services & BPO', 'SME Corporate Parks', 'EdTech & SaaS Startups', 'Media & Entertainment', 'Co-working & Flex Spaces'],
    distanceFromFactory: '~32 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Dedicated installation crews for multi-floor tech park deployments across Andheri–Powai corridor',
    seoTitle: 'Office Furniture Andheri Powai | Workstations for Tech Parks & BPOs | MFS',
    seoDescription: 'Modular office furniture for Andheri & Powai tech parks. Factory-direct workstations, phone pods & storage. 10-14 day express delivery from Bhiwandi. ISO 9001:2015 & IGBC certified. On-site installation.',
    seoKeywords: 'office furniture andheri, office furniture powai, modular workstations andheri east, tech park furniture powai, BPO office furniture mumbai',
    faqPageKey: 'locationAndheriPowai',
    interlinkKey: 'locationAndheriPowai',
  },
  'navi-mumbai': {
    slug: 'office-furniture-navi-mumbai',
    city: 'Navi Mumbai',
    fullCity: 'Navi Mumbai — Airoli, Vashi, Belapur, Ghansoli',
    state: 'Maharashtra',
    tier: 'Data Centres & Large-Scale Tech Corridor',
    heroTagline: 'Office Furniture for Navi Mumbai\'s Expanding Tech & Data Corridor',
    heroDescription: 'High-density modular workstations, server-room-adjacent furniture, and industrial-grade storage solutions — built for the data centres, IT SEZs, and corporate expansions along the Navi Mumbai corridor. Shortest logistics from our Bhiwandi factory.',
    microMarkets: ['Airoli Knowledge Park', 'Vashi Business District', 'CBD Belapur', 'Ghansoli IT Park', 'Rabale MIDC'],
    targetAudience: 'Data Centre Operators, IT SEZ Tenants, Large Corporates, Government Tech Parks, Logistics HQs',
    keyIndustries: ['Data Centres & Cloud Infra', 'IT SEZ Campuses', 'Logistics & Shipping HQs', 'Government & PSU Tech', 'Engineering & Manufacturing R&D'],
    distanceFromFactory: '~45 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Bulk deployment teams for 100+ seat rollouts in Navi Mumbai tech campuses and SEZ zones',
    seoTitle: 'Office Furniture Navi Mumbai | Workstations for IT Parks & Data Centres | MFS',
    seoDescription: 'Buy modular office furniture in Navi Mumbai. Factory-direct from Bhiwandi hub. Workstations, acoustic pods, storage. Express 10-14 day delivery. Certified on-site installation for tech campuses.',
    seoKeywords: 'office furniture navi mumbai, modular workstations airoli, office furniture vashi belapur, data centre furniture navi mumbai, IT park furniture navi mumbai',
    faqPageKey: 'locationNaviMumbai',
    interlinkKey: 'locationNaviMumbai',
  },
  'pune': {
    slug: 'office-furniture-pune',
    city: 'Pune',
    fullCity: 'Pune — Hinjewadi, Kharadi, Baner, Wakad',
    state: 'Maharashtra',
    tier: 'IT Park Proximity Expansion Market',
    heroTagline: 'Office Furniture for Pune\'s IT Parks — Hinjewadi, Kharadi & Beyond',
    heroDescription: 'Modular workstation clusters, acoustic privacy pods, and premium storage — designed for the high-growth IT campuses of Hinjewadi, Kharadi, and Magarpatta. Manufactured in our Mumbai-region factory, delivered to Pune on express logistics.',
    microMarkets: ['Hinjewadi IT Park (Phase I–III)', 'Kharadi IT Corridor', 'Magarpatta Cybercity', 'Baner–Balewadi Tech Zone', 'Wakad Commercial Hub'],
    targetAudience: 'IT/ITES Companies, GCC Offices, Startup Scale-ups, R&D Centres, Managed Office Operators',
    keyIndustries: ['IT & Software Services', 'Global Capability Centres (GCC)', 'Automotive R&D', 'Fintech & SaaS', 'Managed Office & Flex Space'],
    distanceFromFactory: '~165 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Pune-dedicated installation team with certified technicians for Hinjewadi and Kharadi IT park deployments',
    seoTitle: 'Office Furniture Pune | Modular Workstations for Hinjewadi & Kharadi IT Parks | MFS',
    seoDescription: 'Premium modular office furniture for Pune IT parks. Workstations, privacy pods, storage — factory-direct from Bhiwandi. 10-14 day express delivery. ISO 9001:2015 & IGBC certified. On-site installation in Hinjewadi & Kharadi.',
    seoKeywords: 'office furniture pune, modular workstations hinjewadi, office furniture kharadi, IT park furniture pune, acoustic pods pune',
    faqPageKey: 'locationPune',
    interlinkKey: 'locationPune',
  },
  'bengaluru': {
    slug: 'office-furniture-bengaluru',
    city: 'Bengaluru',
    fullCity: 'Bengaluru — Whitefield, Electronic City, Outer Ring Road',
    state: 'Karnataka',
    tier: 'High-Volume Tech Capital & GCC Campus Scale',
    heroTagline: 'Office Furniture for Bengaluru\'s Tech Capital & GCC Campuses',
    heroDescription: 'Large-scale modular workstation rollouts, enterprise acoustic pods, and heavy-duty storage systems — purpose-built for Bengaluru\'s massive tech campuses, GCC floors, and unicorn headquarters. Factory-direct from our Bhiwandi production hub.',
    microMarkets: ['Whitefield IT Hub', 'Electronic City (Phase I & II)', 'Outer Ring Road (ORR)', 'Manyata Tech Park', 'Sarjapur Road Corridor'],
    targetAudience: 'Global Tech Companies, GCC Campuses, Unicorn HQs, Enterprise IT, Large Co-working Chains',
    keyIndustries: ['Global Tech & Product Companies', 'GCC / Shared Services Centres', 'Unicorn & Late-Stage Startups', 'Enterprise IT Consulting', 'Defence & Aerospace R&D'],
    distanceFromFactory: '~980 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Bengaluru-based certified installation crew for campus-scale deployments in Whitefield, Electronic City, and ORR tech parks',
    seoTitle: 'Office Furniture Bengaluru | Workstations for Tech Campuses & GCC Offices | MFS',
    seoDescription: 'Buy modular office furniture in Bengaluru. Factory-direct workstations, acoustic pods, storage for IT parks. Express 10-14 day delivery from Bhiwandi. ISO 9001:2015 & IGBC certified. On-site installation across Bengaluru.',
    seoKeywords: 'office furniture bengaluru, modular workstations whitefield, office furniture electronic city, GCC office furniture bangalore, tech campus furniture bengaluru',
    faqPageKey: 'locationBengaluru',
    interlinkKey: 'locationBengaluru',
  },
  'hyderabad': {
    slug: 'office-furniture-hyderabad',
    city: 'Hyderabad',
    fullCity: 'Hyderabad — HITEC City, Gachibowli, Financial District',
    state: 'Telangana',
    tier: 'Mega-Scale Corporate Layouts & Financial Districts',
    heroTagline: 'Office Furniture for Hyderabad\'s Corporate Mega-Layouts & Financial District',
    heroDescription: 'Enterprise-scale modular workstations, executive meeting pods, and compliance-grade storage — tailored for Hyderabad\'s mega-corporate campuses, financial district offices, and pharma-tech R&D floors. Direct from Bhiwandi to HITEC City.',
    microMarkets: ['HITEC City', 'Gachibowli IT Corridor', 'Financial District', 'Nanakramguda', 'Madhapur Tech Hub'],
    targetAudience: 'Tech MNCs, Financial Services, Pharma-Tech R&D, Government IT, Large-Scale GCC',
    keyIndustries: ['IT & Tech MNCs', 'Financial Services & Banks', 'Pharmaceutical R&D', 'Government IT & Defence', 'GCC & Shared Services'],
    distanceFromFactory: '~710 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Hyderabad-certified installation team for large-floor deployments in HITEC City, Gachibowli & Financial District',
    seoTitle: 'Office Furniture Hyderabad | Workstations for HITEC City & Financial District | MFS',
    seoDescription: 'Premium modular office furniture for Hyderabad. Workstations, acoustic pods, storage — factory-direct from Bhiwandi. 10-14 day express delivery. ISO 9001:2015 & IGBC certified. On-site installation in HITEC City & Gachibowli.',
    seoKeywords: 'office furniture hyderabad, modular workstations hitec city, office furniture gachibowli, financial district furniture hyderabad, corporate furniture hyderabad',
    faqPageKey: 'locationHyderabad',
    interlinkKey: 'locationHyderabad',
  },
  'gurugram': {
    slug: 'office-furniture-gurugram',
    city: 'Gurugram',
    fullCity: 'Gurugram — Cyber City, Golf Course Road, Udyog Vihar',
    state: 'Haryana',
    tier: 'Multinational Corporate Fit-Outs & Modern Layouts',
    heroTagline: 'Office Furniture for Gurugram\'s MNC Fit-Outs & Modern Workspaces',
    heroDescription: 'Architect-grade modular workstations, premium acoustic pods, and space-efficient storage — designed for Gurugram\'s multinational corporate fit-outs, modern co-working layouts, and enterprise campus rollouts. Factory-direct from Bhiwandi.',
    microMarkets: ['Cyber City (DLF Phase I–III)', 'Golf Course Road', 'Udyog Vihar', 'Sohna Road Commercial Belt', 'Sector 44–48 Business Hub'],
    targetAudience: 'MNC Regional HQs, Enterprise Fit-Out Projects, Modern Co-working Operators, Consulting & Audit Firms',
    keyIndustries: ['MNC Regional Headquarters', 'Management Consulting & Audit', 'BFSI & FinTech', 'FMCG & Consumer Tech', 'Real Estate & Co-working'],
    distanceFromFactory: '~1,400 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Gurugram-based certified installation crew for corporate tower fit-outs in Cyber City, Golf Course Road & Udyog Vihar',
    seoTitle: 'Office Furniture Gurugram | Workstations for MNC Offices & Corporate Fit-Outs | MFS',
    seoDescription: 'Buy modular office furniture in Gurugram. Factory-direct workstations, acoustic pods, storage for MNC offices. Express 10-14 day delivery from Bhiwandi. ISO 9001:2015 & IGBC certified. On-site installation in Cyber City & Golf Course Road.',
    seoKeywords: 'office furniture gurugram, modular workstations cyber city, office furniture golf course road gurgaon, MNC furniture gurugram, corporate fit-out furniture gurgaon',
    faqPageKey: 'locationGurugram',
    interlinkKey: 'locationGurugram',
  },
  'mumbai': {
    slug: 'office-furniture-mumbai',
    city: 'Mumbai',
    fullCity: 'Mumbai — Lower Parel, Worli, Goregaon, Malad, Vikhroli',
    state: 'Maharashtra',
    tier: 'India\'s Commercial Capital & Enterprise Hub',
    heroTagline: 'Office Furniture for Mumbai — India\'s Business Capital',
    heroDescription: 'Complete modular office solutions for Mumbai\'s sprawling commercial landscape — from Lower Parel high-rises to Goregaon IT parks. Workstations, acoustic pods, and storage delivered directly from our Bhiwandi factory with certified on-site installation.',
    microMarkets: ['Lower Parel', 'Worli', 'Goregaon East', 'Malad West', 'Vikhroli', 'Kurla', 'Wadala'],
    targetAudience: 'Corporate Offices, Media Houses, Advertising Agencies, Financial Firms, Government Offices',
    keyIndustries: ['Media & Entertainment', 'Advertising & Creative', 'Banking & Financial Services', 'Textile & Trading Houses', 'Government & PSU Offices'],
    distanceFromFactory: '~30–50 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Mumbai-wide certified installation team covering all commercial zones — available for weekday, weekend & after-hours deployments',
    seoTitle: 'Office Furniture Mumbai | Modular Workstations & Storage | Factory Direct | MFS',
    seoDescription: 'Buy modular office furniture in Mumbai. Factory-direct from Bhiwandi hub. Workstations, acoustic pods, storage. Express 10-14 day delivery. ISO 9001:2015 & IGBC certified. Free on-site installation across Mumbai.',
    seoKeywords: 'office furniture mumbai, modular workstations mumbai, office furniture lower parel, corporate furniture mumbai, buy office furniture mumbai',
    faqPageKey: 'locationMumbai',
    interlinkKey: 'locationMumbai',
  },
  'thane': {
    slug: 'office-furniture-thane',
    city: 'Thane',
    fullCity: 'Thane — Ghodbunder Road, Wagle Estate, Majiwada, Kolshet',
    state: 'Maharashtra',
    tier: 'Emerging Corporate Corridor & Manufacturing Proximity',
    heroTagline: 'Office Furniture for Thane\'s Growing Corporate Corridor',
    heroDescription: 'Modular workstations, compact storage systems, and privacy booths for Thane\'s rapidly expanding business districts. Shortest delivery route from our Bhiwandi factory — just 15 km away, ensuring fastest turnaround of any city we serve.',
    microMarkets: ['Ghodbunder Road', 'Wagle Estate MIDC', 'Majiwada–Manpada', 'Kolshet Road', 'Pokhran Road'],
    targetAudience: 'SME Corporates, Manufacturing R&D, IT Services, Pharma Offices, Co-working Spaces',
    keyIndustries: ['SME & Mid-Market Corporates', 'Manufacturing & Engineering', 'IT Services & BPO', 'Pharmaceutical Offices', 'Emerging Co-working Hubs'],
    distanceFromFactory: '~15 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Fastest delivery in our network — Bhiwandi to Thane in under 30 minutes. Same-week installation available for in-stock configurations',
    seoTitle: 'Office Furniture Thane | Modular Workstations Near Bhiwandi Factory | MFS',
    seoDescription: 'Buy modular office furniture in Thane. Closest city to our Bhiwandi factory — fastest delivery. Workstations, pods, storage. 10-14 day turnaround. ISO 9001:2015 & IGBC certified. On-site installation.',
    seoKeywords: 'office furniture thane, modular workstations thane, office furniture ghodbunder road, corporate furniture thane, workstations near bhiwandi',
    faqPageKey: 'locationThane',
    interlinkKey: 'locationThane',
  },
  'chennai': {
    slug: 'office-furniture-chennai',
    city: 'Chennai',
    fullCity: 'Chennai — OMR, Guindy, Sholinganallur, Ambattur',
    state: 'Tamil Nadu',
    tier: 'IT Corridor & Automotive Manufacturing Hub',
    heroTagline: 'Office Furniture for Chennai\'s IT Corridor & Industrial Campuses',
    heroDescription: 'Scalable modular workstations, server-adjacent furniture, and industrial-grade storage — engineered for Chennai\'s IT corridors along OMR, automotive R&D campuses, and manufacturing headquarters. Factory-direct from Bhiwandi to Chennai.',
    microMarkets: ['Old Mahabalipuram Road (OMR)', 'Guindy Industrial Estate', 'Sholinganallur IT Park', 'Ambattur Industrial Estate', 'Perungudi–Taramani'],
    targetAudience: 'IT/ITES Companies, Automotive OEMs, Manufacturing HQs, Banking & Insurance, Shared Services Centres',
    keyIndustries: ['IT & Software Services', 'Automotive & Manufacturing', 'Banking & Insurance', 'Shared Services Centres', 'Healthcare & Pharma'],
    distanceFromFactory: '~1,330 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Chennai-based certified installation team for IT park and industrial campus deployments along OMR, Guindy & Ambattur zones',
    seoTitle: 'Office Furniture Chennai | Workstations for IT Parks & Industrial Campuses | MFS',
    seoDescription: 'Buy modular office furniture in Chennai. Factory-direct from Bhiwandi. Workstations, acoustic pods, storage for OMR IT parks. Express 10-14 day delivery. ISO 9001:2015 & IGBC certified. On-site installation.',
    seoKeywords: 'office furniture chennai, modular workstations omr, office furniture guindy, IT park furniture chennai, corporate furniture chennai',
    faqPageKey: 'locationChennai',
    interlinkKey: 'locationChennai',
  },
  'ahmedabad': {
    slug: 'office-furniture-ahmedabad',
    city: 'Ahmedabad',
    fullCity: 'Ahmedabad — SG Highway, Prahlad Nagar, GIFT City, Satellite',
    state: 'Gujarat',
    tier: 'GIFT City Financial Hub & Industrial Growth Corridor',
    heroTagline: 'Office Furniture for Ahmedabad — GIFT City & SG Highway Corporate Belt',
    heroDescription: 'Modular workstations, meeting pods, and enterprise storage for Ahmedabad\'s booming commercial corridors — from GIFT City\'s international financial towers to SG Highway corporate parks. Direct from Bhiwandi factory to Gujarat.',
    microMarkets: ['GIFT City (SEZ)', 'SG Highway Corporate Belt', 'Prahlad Nagar', 'Satellite–Bodakdev', 'Sindhu Bhavan Road'],
    targetAudience: 'Financial Services (GIFT City), Pharma & Chemical HQs, Textile Corporates, IT & SaaS Companies, Trading Houses',
    keyIndustries: ['Financial Services & Banking', 'Pharmaceutical & Chemical', 'Textile & Manufacturing', 'IT & SaaS Companies', 'International Trading (GIFT SEZ)'],
    distanceFromFactory: '~530 km from Bhiwandi Hub',
    expressDelivery: '10–14 business days',
    installationNote: 'Ahmedabad-certified installation team for GIFT City towers, SG Highway offices & Prahlad Nagar commercial complexes',
    seoTitle: 'Office Furniture Ahmedabad | Workstations for GIFT City & SG Highway | MFS',
    seoDescription: 'Buy modular office furniture in Ahmedabad. Factory-direct from Bhiwandi. Workstations, pods, storage for GIFT City & SG Highway. Express 10-14 day delivery. ISO 9001:2015 & IGBC certified. On-site installation.',
    seoKeywords: 'office furniture ahmedabad, modular workstations gift city, office furniture sg highway, corporate furniture ahmedabad, workstations prahlad nagar',
    faqPageKey: 'locationAhmedabad',
    interlinkKey: 'locationAhmedabad',
  },
};

// Shared material specification data
const MATERIAL_SPECS = [
  { component: 'Desk Tops & Panels', material: 'High-Density Particle Board (HDHMR) with 1mm ABS Edge Banding', standard: 'IS 12823 / EN 312', finish: 'Anti-scratch laminate, 0.8mm HPL' },
  { component: 'Steel Frame & Legs', material: '1.2mm CRCA Steel (Cold Rolled Close Annealed)', standard: 'IS 513 / EN 10130', finish: 'Epoxy Powder Coated (60–80 microns)' },
  { component: 'Acoustic Panels (Pods)', material: '40mm PU Foam + Fabric Wrapped Panel', standard: 'IS 15061 / ISO 354 (Sound Absorption)', finish: 'NRC 0.75–0.85 rated' },
  { component: 'Storage & Cabinet Bodies', material: 'Pre-laminated MDF / Particle Board', standard: 'IS 12823 / EN 622', finish: 'Soft-close mechanisms, cam-lock assembly' },
  { component: 'Connectors & Hardware', material: 'Zinc Alloy Die-Cast + SS 304 Fasteners', standard: 'IS 4218 / DIN 933', finish: 'Chrome / Nickel plated' },
];

const ISO_CERTS = [
  { cert: 'ISO 9001:2015', scope: 'Quality Management System — Design, manufacture & delivery of modular office furniture' },
  { cert: 'IGBC Certified', scope: 'Indian Green Building Council — Sustainable, eco-friendly manufacturing and materials for green-rated commercial interiors' },
];

const LEAD_TIMES = [
  { item: 'Standard Modular Workstations (2/4/6 Seater)', lead: '10–14 business days', note: 'Ready-to-ship stock available for select configurations' },
  { item: 'Privacy Booths & Acoustic Pods', lead: '14–18 business days', note: 'Custom colour and panel options add 3–5 days' },
  { item: 'Storage Cabinets & Pedestals', lead: '10–14 business days', note: 'Standard finishes ship within 10 days' },
  { item: 'Bulk Orders (50+ seats)', lead: '14–21 business days', note: 'Dedicated production line allocation' },
  { item: 'Custom / Non-standard Configurations', lead: '21–28 business days', note: 'Includes design approval cycle' },
];

export default function CommercialLocationPage({ locationKey }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const loc = LOCATION_DATA[locationKey];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/products`);
        setProducts(res.data.slice(0, 8));
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [locationKey]);

  if (!loc) return null;

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Office Furniture', url: '/products' },
    { name: `Office Furniture ${loc.city}` },
  ];

  const locationFaqs = [
    { question: `Do you deliver and install office furniture in ${loc.city}?`, answer: `Yes. All orders ship directly from our Bhiwandi (Mumbai) factory hub to ${loc.fullCity}. Delivery includes white-glove on-site installation by our certified technicians within ${loc.expressDelivery}.` },
    { question: `What is the delivery timeline for ${loc.city}?`, answer: `Express commercial turnaround: ${loc.expressDelivery} from order confirmation. This includes manufacturing, quality inspection, secure transit from Bhiwandi, and certified on-site installation at your ${loc.city} office.` },
    { question: `Are your products ISO 9001:2015 & IGBC certified for corporate procurement?`, answer: `Yes. Our manufacturing holds ISO 9001:2015 (Quality Management System) certification and IGBC (Indian Green Building Council) certification for sustainable, eco-friendly manufacturing. Full compliance documentation is available for corporate procurement teams.` },
    { question: `Do you handle large-scale fit-outs (50+ workstations) in ${loc.city}?`, answer: `Absolutely. We regularly execute 50–500+ seat rollouts for corporates. ${loc.installationNote}. Bulk orders get dedicated production line allocation and a single-point project coordinator.` },
    { question: `Can I get a Proforma Invoice for internal procurement approval?`, answer: `Yes — generate a formal Proforma Invoice directly from our website by adding products to cart. The PI includes GST breakup, payment terms, delivery timelines, and can be downloaded as a professional PDF for your procurement or finance team.` },
    { question: `What materials do you use for workstations delivered to ${loc.city}?`, answer: `Desk surfaces use High-Density HDHMR board with 1mm ABS edge banding (IS 12823). Steel frames are 1.2mm CRCA steel with 60-80 micron epoxy powder coating (IS 513). All hardware is Zinc Alloy / SS 304 grade.` },
  ];

  // ServiceAreaStructuredData for this location
  const serviceAreaSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Modular Office Furniture Supply, Delivery & Installation",
    "provider": {
      "@type": "Organization",
      "name": "Modular Furniture Store",
      "url": SITE_URL,
    },
    "areaServed": {
      "@type": "City",
      "name": loc.city,
      "containedInPlace": { "@type": "State", "name": loc.state }
    },
    "description": loc.heroDescription,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "INR",
        "minPrice": "5214",
        "description": "Starting from INR 5,214 per seat for modular workstations"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `Office Furniture for ${loc.city}`,
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Modular Workstations", "description": "Linear, L-shaped, and cluster configurations for 2-8 seaters" } },
        { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Acoustic Privacy Pods", "description": "1-6 seater soundproof pods with 30-40dB noise reduction" } },
        { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Storage Cabinets", "description": "Sliding door, filing, pedestal, and credenza storage systems" } },
      ]
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": locationFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`location-page-${locationKey}`}>
      <SEO
        title={loc.seoTitle}
        description={loc.seoDescription}
        keywords={loc.seoKeywords}
        canonical={`/${loc.slug}`}
      />
      <BreadcrumbStructuredData items={breadcrumbs} />
      <LocalBusinessStructuredData />
      {products.length > 0 && <ItemListStructuredData products={products} listName={`Office Furniture in ${loc.city}`} />}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceAreaSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <PremiumHeader />

      {/* ── HERO ── */}
      <section className="bg-[#1a1a1a] text-white py-16 md:py-20" data-testid="location-hero">
        <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
          <div className="flex items-center gap-2 text-[#c5a059] text-xs font-bold uppercase tracking-[0.2em] mb-4">
            <MapPin className="w-4 h-4" />
            <span>{loc.tier}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-4 max-w-4xl">
            {loc.heroTagline}
          </h1>
          <p className="text-gray-300 text-base sm:text-lg max-w-3xl leading-relaxed mb-8">
            {loc.heroDescription}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/products">
              <Button className="bg-[#c5a059] hover:bg-[#b08d45] text-white rounded-none px-8 py-5 text-sm tracking-[0.1em] font-bold" data-testid="hero-browse-btn">
                BROWSE CATALOGUE
              </Button>
            </Link>
            <Link to="/bulk-inquiry">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-none px-8 py-5 text-sm tracking-[0.1em] font-bold" data-testid="hero-bulk-btn">
                REQUEST BULK QUOTE
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── QUICK STATS BAR ── */}
      <section className="border-b border-gray-100 bg-[#faf9f6]" data-testid="quick-stats">
        <div className="container mx-auto px-4 sm:px-8 max-w-6xl py-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Factory Hub</p>
              <p className="text-sm font-extrabold text-[#1a1a1a] mt-1">Bhiwandi, Maharashtra</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Express Delivery</p>
              <p className="text-sm font-extrabold text-[#1a1a1a] mt-1">{loc.expressDelivery}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Distance</p>
              <p className="text-sm font-extrabold text-[#1a1a1a] mt-1">{loc.distanceFromFactory}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Installation</p>
              <p className="text-sm font-extrabold text-[#c5a059] mt-1">On-Site Certified</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-8 max-w-6xl py-12 md:py-16">

        {/* ── MICRO-MARKETS SERVED ── */}
        <section className="mb-14" data-testid="micro-markets">
          <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight mb-4">
            Micro-Markets We Serve in {loc.city}
          </h2>
          <div className="flex flex-wrap gap-2">
            {loc.microMarkets.map((m, i) => (
              <span key={i} className="px-3 py-1.5 bg-[#f5f3ee] text-[#1a1a1a] text-xs font-semibold border border-gray-200">
                {m}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-3">
            <strong>Target Audience:</strong> {loc.targetAudience}
          </p>
        </section>

        {/* ── KEY INDUSTRIES ── */}
        <section className="mb-14" data-testid="key-industries">
          <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight mb-4">
            Key Industries in {loc.city}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {loc.keyIndustries.map((ind, i) => (
              <div key={i} className="flex items-center gap-2.5 p-3 border border-gray-100 bg-white">
                <Building2 className="w-4 h-4 text-[#c5a059] flex-shrink-0" />
                <span className="text-sm font-medium text-[#1a1a1a]">{ind}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── LOGISTICS & INSTALLATION (KEY SECTION) ── */}
        <section className="mb-14 border border-[#c5a059]/30 bg-[#fdfcf8] p-6 sm:p-8" data-testid="logistics-installation">
          <div className="flex items-center gap-2 mb-5">
            <Truck className="w-5 h-5 text-[#c5a059]" />
            <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight">
              Logistics & Installation — {loc.city}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Shipping Column */}
            <div>
              <h3 className="text-sm font-bold text-[#1a1a1a] uppercase mb-3 flex items-center gap-2">
                <Factory className="w-4 h-4 text-[#c5a059]" /> Direct Shipping
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Origin:</strong> Bhiwandi Factory Hub, Thane District, Maharashtra</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Distance to {loc.city}:</strong> {loc.distanceFromFactory}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Transit Mode:</strong> Dedicated containerised freight for 50+ seats; partload for smaller orders</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Packaging:</strong> Multi-layer corrugated + foam-insert protection (zero damage guarantee)</span>
                </li>
              </ul>
            </div>

            {/* Installation Column */}
            <div>
              <h3 className="text-sm font-bold text-[#1a1a1a] uppercase mb-3 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-[#c5a059]" /> On-Site Installation
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Express Turnaround:</strong> {loc.expressDelivery} (order-to-install)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Crew:</strong> {loc.installationNote}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Method:</strong> Kholo, Jodo, Lagalo — tool-free modular assembly (no drilling, no carpentry)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Post-Install:</strong> On-site verification, floor-level sign-off, and manufacturer warranty activation</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── MATERIAL SPECIFICATIONS TABLE ── */}
        <section className="mb-14" data-testid="material-specs">
          <div className="flex items-center gap-2 mb-5">
            <ShieldCheck className="w-5 h-5 text-[#c5a059]" />
            <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight">
              Material & Build Specifications
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200" data-testid="specs-table">
              <thead>
                <tr className="bg-[#1a1a1a] text-white">
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Component</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Material</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Standard / Cert</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Finish</th>
                </tr>
              </thead>
              <tbody>
                {MATERIAL_SPECS.map((spec, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#faf9f6]'}>
                    <td className="px-4 py-3 font-semibold text-[#1a1a1a] border-b border-gray-100">{spec.component}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{spec.material}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{spec.standard}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{spec.finish}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── ISO CERTIFICATIONS TABLE ── */}
        <section className="mb-14" data-testid="iso-certs">
          <div className="flex items-center gap-2 mb-5">
            <Award className="w-5 h-5 text-[#c5a059]" />
            <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight">
              Certifications & Compliance
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200" data-testid="certs-table">
              <thead>
                <tr className="bg-[#1a1a1a] text-white">
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Certification</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Scope / Application</th>
                </tr>
              </thead>
              <tbody>
                {ISO_CERTS.map((cert, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#faf9f6]'}>
                    <td className="px-4 py-3 font-bold text-[#1a1a1a] border-b border-gray-100 whitespace-nowrap">{cert.cert}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{cert.scope}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── LEAD TIME TABLE ── */}
        <section className="mb-14" data-testid="lead-times">
          <div className="flex items-center gap-2 mb-5">
            <Clock className="w-5 h-5 text-[#c5a059]" />
            <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight">
              Lead Times & Delivery Schedule to {loc.city}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200" data-testid="lead-times-table">
              <thead>
                <tr className="bg-[#1a1a1a] text-white">
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Product Category</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Lead Time</th>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider">Note</th>
                </tr>
              </thead>
              <tbody>
                {LEAD_TIMES.map((lt, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#faf9f6]'}>
                    <td className="px-4 py-3 font-semibold text-[#1a1a1a] border-b border-gray-100">{lt.item}</td>
                    <td className="px-4 py-3 font-bold text-[#c5a059] border-b border-gray-100 whitespace-nowrap">{lt.lead}</td>
                    <td className="px-4 py-3 text-gray-600 border-b border-gray-100">{lt.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── PRODUCTS ── */}
        <section className="mb-14" data-testid="location-products">
          <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight mb-6">
            Featured Products — Available for {loc.city}
          </h2>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#c5a059] mx-auto"></div>
              <p className="mt-4 text-gray-400 text-sm">Loading products...</p>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              <div className="text-center mt-8">
                <Link to="/products">
                  <Button className="bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white rounded-none px-8 py-4 text-sm tracking-[0.1em] font-bold" data-testid="view-all-products-btn">
                    VIEW FULL CATALOGUE <ArrowRight className="w-4 h-4 ml-2 inline" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <p className="text-center py-12 text-gray-400">Products loading...</p>
          )}
        </section>

        {/* ── LOCATION FAQ ── */}
        <section className="mb-14" data-testid="location-faq">
          <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight mb-5">
            Frequently Asked Questions — Office Furniture in {loc.city}
          </h2>
          <div className="space-y-3 max-w-4xl">
            {locationFaqs.map((faq, i) => (
              <details key={i} className="group border border-gray-100 open:border-[#c5a059] transition-colors">
                <summary className="flex justify-between items-center cursor-pointer px-5 py-3.5 text-sm font-semibold text-[#1a1a1a] hover:text-[#c5a059]">
                  {faq.question}
                  <span className="text-[#c5a059] group-open:rotate-45 transition-transform text-lg ml-4 flex-shrink-0">+</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── OTHER LOCATIONS ── */}
        <section className="mb-14" data-testid="other-locations">
          <h2 className="text-base sm:text-lg font-extrabold text-[#1a1a1a] uppercase tracking-tight mb-4">
            We Also Serve
          </h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(LOCATION_DATA)
              .filter(([key]) => key !== locationKey)
              .map(([key, data]) => (
                <Link
                  key={key}
                  to={`/${data.slug}`}
                  className="px-4 py-2 bg-[#f5f3ee] hover:bg-[#c5a059] hover:text-white text-[#1a1a1a] text-xs font-semibold border border-gray-200 transition-colors"
                >
                  {data.city}
                </Link>
              ))}
          </div>
        </section>

        {/* ── CTA BANNER ── */}
        <section className="bg-[#1a1a1a] text-white p-8 sm:p-10 text-center mb-14" data-testid="location-cta">
          <h2 className="text-xl sm:text-2xl font-extrabold mb-3">Need a Custom Quote for Your {loc.city} Office?</h2>
          <p className="text-gray-300 text-sm mb-6 max-w-xl mx-auto">
            Our workspace planning team will create a layout proposal with product recommendations and pricing within 48 hours.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="tel:+919594685818" className="inline-flex items-center gap-2 bg-[#c5a059] text-white px-6 py-3 font-bold text-sm" data-testid="cta-phone">
              <Phone className="w-4 h-4" />
              +91 95946 85818
            </a>
            <a href="mailto:hello@modularfurniturestore.com" className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3 font-bold text-sm hover:bg-white/10 transition-colors" data-testid="cta-email">
              <Mail className="w-4 h-4" />
              hello@modularfurniturestore.com
            </a>
          </div>
        </section>

        {/* ── GLOBAL PAGE FAQ + INTERLINKS ── */}
        <PageFAQ pageKey={loc.faqPageKey} />
        <RelatedPages pageKey={loc.interlinkKey} className="mt-12" />
      </div>

      <PremiumFooter />
    </div>
  );
}

export { LOCATION_DATA };
